import { RowButton } from "@/components/ui/pressable";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TaskMentionText } from "@/components/atomic-crm/tasks/TaskMentionText";
import { cn } from "@/lib/utils";

export const TaskDescriptionCell = ({
  text,
  isDone = false,
  useMentions = true,
  footer,
  fadeSurface = "background",
  enablePreview = true,
}: {
  text?: string | null;
  isDone?: boolean;
  useMentions?: boolean;
  footer?: ReactNode;
  fadeSurface?: "background" | "card";
  /** When false, renders plain text (for use inside another clickable row). */
  enablePreview?: boolean;
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const checkOverflow = () => {
      setIsOverflowing(element.scrollWidth > element.clientWidth + 1);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  if (!text?.trim()) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const renderedText = useMentions ? <TaskMentionText text={text} /> : text;

  const descriptionBody = (
    <div
      ref={contentRef}
      className={cn("truncate pr-8", isDone && "line-through")}
    >
      {renderedText}
    </div>
  );

  return (
    <>
      <div className="min-w-0 max-w-full">
        <div className="relative min-w-0">
          {enablePreview ? (
            <RowButton
              layout="none"
              density="none"
              className={cn(
                "block w-full min-w-0 rounded-sm text-left text-sm transition-opacity hover:opacity-80 focus-visible:",
                isDone && "text-muted-foreground",
              )}
              onClick={() => setPreviewOpen(true)}
              aria-label="View full task description"
            >
              {descriptionBody}
            </RowButton>
          ) : (
            <div
              className={cn(
                "block w-full min-w-0 text-left text-sm",
                isDone && "text-muted-foreground",
              )}
            >
              {descriptionBody}
            </div>
          )}
          {isOverflowing ? (
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l to-transparent",
                fadeSurface === "card"
                  ? "from-card via-card/70"
                  : "from-background via-background/70",
              )}
            />
          ) : null}
        </div>
        {footer ? <div className="mt-1">{footer}</div> : null}
      </div>

      <Dialog open={previewOpen && enablePreview} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Task description</DialogTitle>
          </DialogHeader>
          <div
            className={cn(
              "text-sm leading-relaxed whitespace-pre-wrap break-words",
              isDone && "line-through text-muted-foreground",
            )}
          >
            {renderedText}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
